<template>
    <div>
        <div class="flex flex-col">
            <div
                id="title-general"
                class="flex flex-col md:flex-row justify-between items-center"
            >
                <div class="flex justify-between items-center">
                    <router-link
                        to="/app/carts"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                    <div v-if="id" class="flex justify-between items-center">
                        <h1 class="text-3xl text-theme-primary text-center">
                            &nbsp;Detalle de Carrito
                            <span class="text-theme-primary"> #{{ id }} </span>
                        </h1>
                        <custom-tooltip>
                            <app-button icon border @click="copyTo">
                                🖨
                            </app-button>
                            <template #tooltip>
                                <div class="content-tooltip">
                                    <span> Copiar en portapapeles </span>
                                </div>
                            </template>
                        </custom-tooltip>
                    </div>

                    <h1 v-else>&nbsp; Registrar carro de compras</h1>
                </div>

                <custom-switch
                    v-if="$ability.hasPermission('supplyList:Update') && id"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </div>
        </div>

        <div class="flex">
            <div class="w-full mt-4">
                <p>
                    Recuerda debe de haber inventario en la tienda seleccionada
                    para poder encontrar el carro de compras pre fabricado.
                </p>
            </div>
        </div>

        <div class="flex">
            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    v-model="name"
                    type="text"
                    label="Nombre de carrito"
                    class="w-full"
                    :disabled="!enabledUpdate"
                    :error="
                        !isValidName & introValidators.name
                            ? 'Nombre de Carrito invalido'
                            : ''
                    "
                    @focus="onFocusSomeInput"
                    @blur="onBlurName"
                >
                    <template #icon> 🔑 </template>
                </custom-input>
            </div>
            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    id="begin"
                    v-model="expiredAt"
                    type="date"
                    class="w-full min-w-full"
                    label="Fecha de expiración"
                    :disabled="!enabledUpdate"
                    name="trip-begin"
                    :min="dateMax"
                />
            </div>
        </div>

        <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 mt-8 flex flex-col">
                <div class="flex items-center justify-between">
                    <strong>Vendedor </strong>
                    <custom-tooltip border>
                        <app-button icon circle border> ℹ </app-button>
                        <template #tooltip>
                            Se registran las ventas generadas por el carrito de
                            compras al vendedor.
                        </template>
                    </custom-tooltip>
                </div>
                <custom-input v-model="seller.email" disabled class="w-full" />
            </div>
            <div class="w-full md:w-1/2 px-2 mt-8 flex flex-col">
                <div class="flex items-center justify-between">
                    <strong class="capitalize">
                        {{ merchant.merchantType | merchantInfo("type") }}
                    </strong>
                    <custom-tooltip border>
                        <app-button icon circle border> ℹ </app-button>
                        <template #tooltip>
                            {{
                                enabledUpdate
                                    ? "Puedes cambiar la tienda seleccionandola en la parte superior"
                                    : "Tienda a la que se debe recoger el pedido"
                            }}
                        </template>
                    </custom-tooltip>
                </div>
                <custom-input v-model="merchant.name" class="w-full" disabled />
            </div>
        </div>

        <div
            v-show="enabledUpdate"
            class="flex mt-5 flex-col justify-center items-center"
        >
            <h3 class="text-lg">Selecciona las referencias del carrito</h3>
            <references-search-scanner
                :enabled-scanner="enabledScanner"
                :default-filters="{ active: true }"
                @input="validatePushReferences"
            />
        </div>

        <div class="z-0 mt-3">
            <h4 v-show="references.length" class="mx-2 mt-3">
                Precio de Variantes
                <strong class="text-theme-primary">
                    {{ total | money }}
                </strong>
            </h4>
            <reference-table-item
                v-for="(reference, index) in references"
                :key="index"
                :enabled="enabledUpdate"
                :reference="reference"
                class="md:w-11/12 w-full mx-auto"
                @update="updateReferenceOfCart"
                @remove="removeReference"
                @focus="onFocusSomeInput"
            />
        </div>

        <div
            v-if="references.length > 0 && enabledUpdate"
            class="flex z-0 my-4"
        >
            <div class="w-full flex justify-center">
                <app-button
                    v-if="!id"
                    size="large"
                    @click="createItemComponent"
                >
                    Crear carrito
                </app-button>
                <app-button v-else @click="updateItemComponent">
                    Actualizar carrito
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions, mapGetters, mapState } from "vuex";

import { nameRegex } from "@/common/lib/regex";

import ReferenceTableItem from "@/components/references/ReferenceTableItem.vue";
import ReferencesSearchScanner from "@/components/references/ReferencesSearchScanner.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "CartsDetails",
    components: {
        ReferenceTableItem,
        ReferencesSearchScanner,
        CustomInput
    },
    data: () => ({
        dateMax: DateTime.now().toFormat("yyyy-MM-dd"),
        id: 0,
        total: 0,
        name: "",
        expiredAt: null,
        references: [],
        seller: { email: "" },
        merchant: { name: "", merchantId: "", merchantType: "store" },
        enabledScanner: false,
        enabledUpdate: false,
        introValidators: {
            name: false
        }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("auth", ["user"]),
        isValidName() {
            return nameRegex.test(this.name);
        }
    },
    watch: {
        selectedMerchant({ id, name, type }) {
            if (this.enabledUpdate) {
                this.merchant = {
                    name,
                    merchantId: id,
                    merchantType: type
                };
            }
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (!(await verifyPagePermission("cart:Get", "/app/carts"))) return;
            const id = Number(this.$route.params.id);
            this.id = id;
            await this.getBaseItem(id);
        } else if ("idClone" in this.$route.params) {
            if (!(await verifyPagePermission("cart:Create", "/app/carts")))
                return;
            this.id = 0;
            this.enabledUpdate = true;
            const idTmp = Number(this.$route.params.idClone);
            await this.getBaseItem(idTmp);
            this.name = `${this.name} CLONE`;
            const { merchantId, merchantType, name } = this.selectedMerchant;
            this.merchant = { merchantId, merchantType, name };
            this.seller.email = this.user.email;
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas clonando un carro de compras",
                icon: "info",
                showCloseButton: true
            });
        } else {
            await verifyPagePermission("cart:Create", "/app/carts");
            this.seller.email = this.user.email;
            const { merchantId, merchantType, name } = this.selectedMerchant;
            this.merchant = { merchantId, merchantType, name };
            this.enabledUpdate = true;
            this.enabledScanner = true;
        }
    },
    methods: {
        ...mapActions("carts", ["getCartById", "updateCart", "createCart"]),
        async getBaseItem(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { expiredAt, merchant, name, references, seller } =
                    await this.getCartById({ id });
                this.expiredAt = expiredAt;
                this.name = name;
                this.merchant = merchant;
                this.seller = seller;
                this.references = [...references];
                this.calculateTotals();
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/carts");
                }
            }
        },
        async updateItemComponent() {
            if (!this.validateData()) {
                return this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Carrito invalido",
                    text: "Revisa por favor que todo este completo",
                    icon: "info"
                });
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateCart(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createItemComponent() {
            if (!this.validateData()) {
                return this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Carrito invalido",
                    text: "Revisa por favor que todo este completo",
                    icon: "info"
                });
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createCart(this.getDataSend());
                this.$loading.hide();
                this.$sound.play();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `El enlace a sido copiado a tu porta papeles: ${this.$publicUrl}/checkout/${data.id}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de carritos",
                    cancelButtonText: "Nuevo Carrito"
                });
                this.id = data.id;
                this.copyTo();
                if (dialogResult.isConfirmed) {
                    this.id = data.id;
                    this.$router.push(`/app/carts/detail/${this.id}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/carts/");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        name: false
                    };
                    this.name = "";
                    this.references = [];
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async copyTo() {
            await navigator.clipboard.writeText(
                `${this.$publicUrl}/checkout/${this.id}`
            );
            this.$toast.success({
                title: "Copiado al porta papeles"
            });
        },
        validateData() {
            this.introValidators = {
                name: true
            };
            return this.isValidName && this.references.length;
        },
        calculateTotals() {
            this.total = this.references.reduce(
                (a, b) => a + b.priceOffer * b.quantity,
                0
            );
        },
        getDataSend() {
            return {
                id: this.id,
                name: this.name,
                merchantId: this.merchant.merchantId,
                merchantType: this.merchant.merchantType,
                expiredAt: this.expiredAt,
                references: this.references.map((item) => ({
                    id: item.id,
                    quantity: item.quantity
                }))
            };
        },
        validatePushReferences(reference) {
            const index = this.references.findIndex(
                (item) => item.id === reference.id
            );

            if (index === -1) {
                this.references.unshift({ ...reference, quantity: 1 });
            } else {
                let references = [...this.references];
                references[index] = {
                    ...references[index],
                    quantity: references[index].quantity + 1
                };
                this.references = [...references];
            }
            this.calculateTotals();
        },
        updateReferenceOfCart({ appliedDiscountPerReference, id, quantity }) {
            this.onBlurSomeInput();
            const index = this.references.findIndex((item) => item.id == id);
            let references = [...this.references];
            references[index] = {
                ...references[index],
                quantity,
                appliedDiscountPerReference
            };
            this.references = [...references];
            this.calculateTotals();
        },
        removeReference({ id }) {
            const index = this.references.findIndex((item) => item.id == id);
            let references = [...this.references];
            references.splice(index, 1);
            this.references = [...references];
            this.calculateTotals();
        },
        onBlurName() {
            this.introValidators.name = true;
            this.onBlurSomeInput();
        },
        onBlurSomeInput() {
            this.enabledScanner = true;
        },
        onFocusSomeInput() {
            this.enabledScanner = false;
        }
    }
};
</script>
